import useQueryString from '@hooks/useQueryString';
import {
  Box,
  Button,
  FormControl,
  Grid,
  MenuItem,
  OutlinedInput,
  Popover,
  Select,
  SelectChangeEvent
} from '@mui/material';
import { filterEnumToString } from '@utils/ndiEnumToString';
import {
  IndustrialCompanyScale,
  IcxRegion,
  IndustrialComplexName,
  IndustrialSectorsCategory,
  IndustrialSectorsName
} from '@generated/graphql';
import { useRef } from 'react';

interface SearchFilterPopperProps {
  isOpen: boolean;
  anchorEl: null | HTMLElement;
  categories: { category: string; values: any[] }[];
  onClose: () => void;
}

const SearchFilterPopper = ({
  categories = [],
  isOpen,
  anchorEl,
  onClose
}: SearchFilterPopperProps) => {
  const { onMoveQueryString, query } = useQueryString<{
    [key: string]: string[] | string;
  }>();

  const handleReset = () => {
    const withoutCategoryQuery = query;
    categories.forEach((category) => {
      delete withoutCategoryQuery[category.category];
    });
    onMoveQueryString({ ...withoutCategoryQuery, page: '1' });
  };

  return (
    <Popover
      open={isOpen}
      anchorEl={anchorEl}
      onClose={onClose}
      sx={{ borderRadius: 32 }}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left'
      }}
    >
      <Box
        bgcolor={'white'}
        width={430}
        padding={2}
        display={'flex'}
        justifyContent={'center'}
        boxShadow={`0px 24px 24px -6px rgba(0, 0, 0, 0.2), 0px 24px 56px 4px rgba(0, 0, 0, 0.14), 0px 14px 56px 7px rgba(0, 0, 0, 0.12)`}
      >
        <Grid container justifyContent={'center'}>
          <Grid container spacing={1} height={400}>
            {categories.map(({ category, values }) => (
              <Category key={category} category={category} values={values} />
            ))}
          </Grid>
          <Button variant="text" onClick={handleReset}>
            전체 초기화
          </Button>
        </Grid>
      </Box>
    </Popover>
  );
};

export default SearchFilterPopper;

interface CateogryProps {
  category: string;
  values: any[];
}
const Category = ({ category, values }: CateogryProps) => {
  const { onUpdateQueryString, query } = useQueryString<{
    [key: string]: string[] | string;
  }>();
  const ref = useRef<HTMLLIElement | null>(null);

  const categoryQueryString = query[category] ?? [];
  const selectedValues =
    typeof categoryQueryString === 'string'
      ? [categoryQueryString]
      : categoryQueryString;

  const isAllSelected = selectedValues.length === values.length;

  const handleChange = (event: SelectChangeEvent<string[]>) => {
    const {
      target: { value }
    } = event;
    onUpdateQueryString({ [category]: value, page: '1' });
  };

  const handleUnitReset = () => {
    onUpdateQueryString({ [category]: [], page: '1' });
  };

  const handleClickAllItem = (event: React.MouseEvent<HTMLLIElement>) => {
    event.preventDefault();

    onUpdateQueryString({ [category]: isAllSelected ? [] : values, page: '1' });

    setTimeout(() => {
      ref?.current?.focus();
    }, 1);
  };

  return (
    <Grid container alignItems={'center'} flexWrap={'nowrap'}>
      <Grid item container justifyContent={'center'} xs={2}>
        {category}
      </Grid>
      <Grid item container justifyContent={'center'} xs={10}>
        <FormControl sx={{ width: 300 }}>
          <Select
            multiple
            displayEmpty
            value={selectedValues}
            disabled={!values.length}
            onChange={handleChange}
            input={<OutlinedInput />}
            renderValue={(selected) => {
              if (selected.length === 0) {
                return <em>{category} 전체</em>;
              }
              return selected
                .map((selected) =>
                  filterEnumToString(
                    selected as
                      | IcxRegion
                      | IndustrialCompanyScale
                      | IndustrialSectorsName
                      | IndustrialSectorsCategory
                      | IndustrialComplexName
                  )
                )
                .join(', ');
            }}
            MenuProps={MenuProps}
            inputProps={{ 'aria-label': 'Without label' }}
          >
            <MenuItem disabled value="">
              <em>{category} 선택</em>
            </MenuItem>
            <MenuItem ref={ref} onClick={handleClickAllItem}>
              {isAllSelected ? '전체 해제' : '전체 선택'}
            </MenuItem>
            {values.map((value) => (
              <MenuItem key={value} value={value}>
                {filterEnumToString(value)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item container justifyContent={'center'} xs={2}>
        <Button
          variant="text"
          disabled={!selectedValues.length}
          onClick={handleUnitReset}
        >
          초기화
        </Button>
      </Grid>
    </Grid>
  );
};

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250
    }
  }
};
